import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff, Save, Key, Sparkles, Zap, Loader2, CheckCircle, AlertCircle, Send } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface SystemSetting {
  key: string;
  value: string | null;
}

const ApiSettings = () => {
  const { toast } = useToast();
  const [showKieApi, setShowKieApi] = useState(false);
  const [showGeminiApi, setShowGeminiApi] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const [kieApiKey, setKieApiKey] = useState("");
  const [geminiApiKey, setGeminiApiKey] = useState("");
  const [kieApiEndpoint, setKieApiEndpoint] = useState("https://api.kie.ai");
  const [kieCallbackUrl, setKieCallbackUrl] = useState("");
  const [telegramBotToken, setTelegramBotToken] = useState("");
  const [telegramChatId, setTelegramChatId] = useState("");
  const [showTelegramToken, setShowTelegramToken] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from("system_settings" as any)
        .select("key, value")
        .in("key", ["KIE_API_KEY", "GEMINI_API_KEY", "KIE_API_ENDPOINT", "KIE_CALLBACK_URL", "TELEGRAM_BOT_TOKEN", "TELEGRAM_CHAT_ID"]);

      if (error) {
        console.error("Error loading settings:", error);
      }

      const dbSettings = (data as unknown as SystemSetting[]) || [];
      const getVal = (key: string) => dbSettings.find(s => s.key === key)?.value;

      // Security: Load from database only (no localStorage for sensitive keys)
      setKieApiKey(getVal("KIE_API_KEY") || "");
      setGeminiApiKey(getVal("GEMINI_API_KEY") || "");
      setKieApiEndpoint(getVal("KIE_API_ENDPOINT") || "https://api.kie.ai");
      setKieCallbackUrl(getVal("KIE_CALLBACK_URL") || "");
      setTelegramBotToken(getVal("TELEGRAM_BOT_TOKEN") || "");
      setTelegramChatId(getVal("TELEGRAM_CHAT_ID") || "");

      if (data && data.length > 0) {
        // toast.success("Loaded settings from database");
      }
    } catch (err) {
      console.error("Error loading settings:", err);
    } finally {
      setIsLoading(false);
    }
  };

  // Save logic
  const handleSave = async () => {
    setIsSaving(true);
    try {
      const settings = [
        { key: "KIE_API_KEY", value: kieApiKey },
        { key: "GEMINI_API_KEY", value: geminiApiKey },
        { key: "KIE_API_ENDPOINT", value: kieApiEndpoint },
        { key: "KIE_CALLBACK_URL", value: kieCallbackUrl },
        { key: "TELEGRAM_BOT_TOKEN", value: telegramBotToken },
        { key: "TELEGRAM_CHAT_ID", value: telegramChatId },
      ];

      // Security: Save to database only (no localStorage for sensitive API keys)
      let allSuccess = true;
      for (const setting of settings) {
        const { error } = await supabase
          .from("system_settings" as any)
          .upsert({ key: setting.key, value: setting.value }, { onConflict: "key" });

        if (error) {
          console.error(`Error saving ${setting.key}:`, error);
          allSuccess = false;
        }
      }

      if (allSuccess) {
        toast({
          title: "Settings Saved",
          description: "API configurations have been saved securely to database.",
        });
      } else {
        throw new Error("Failed to save some settings to database");
      }
    } catch (err) {
      console.error("Error saving settings:", err);
      toast({
        title: "Save Failed",
        description: "Failed to save settings. Check admin permissions and try again.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Kie.ai API Settings */}
      <Card variant="glass">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <Zap className="h-5 w-5 text-primary" />
            </div>
            <div>
              <CardTitle>Kie.ai API Configuration</CardTitle>
              <CardDescription>Configure your Kie.ai API for content generation</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="kie-endpoint">API Endpoint</Label>
            <Input
              id="kie-endpoint"
              value={kieApiEndpoint}
              onChange={(e) => setKieApiEndpoint(e.target.value)}
              placeholder="https://api.kie.ai"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="kie-callback-url">Callback URL (Optional)</Label>
            <Input
              id="kie-callback-url"
              value={kieCallbackUrl}
              onChange={(e) => setKieCallbackUrl(e.target.value)}
              placeholder="https://your-domain.com/api/callback"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="kie-api-key">API Key</Label>
            <div className="relative">
              <Input
                id="kie-api-key"
                type={showKieApi ? "text" : "password"}
                value={kieApiKey}
                onChange={(e) => setKieApiKey(e.target.value)}
                placeholder="Enter your Kie.ai API key"
                className="pr-10"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute right-0 top-0 h-full px-3"
                onClick={() => setShowKieApi(!showKieApi)}
              >
                {showKieApi ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>
            </div>
            {kieApiKey && (
              <div className="flex items-center gap-2 text-sm text-green-500">
                <CheckCircle className="h-4 w-4" />
                <span>API Key configured</span>
              </div>
            )}
            {!kieApiKey && (
              <div className="flex items-center gap-2 text-sm text-yellow-500">
                <AlertCircle className="h-4 w-4" />
                <span>No API Key set</span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Key className="h-4 w-4" />
            <span>Get your API key from <a href="https://kie.ai" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">kie.ai</a></span>
          </div>
        </CardContent>
      </Card>

      {/* Gemini API Settings */}
      <Card variant="glass">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-secondary/10 flex items-center justify-center">
              <Sparkles className="h-5 w-5 text-secondary" />
            </div>
            <div>
              <CardTitle>Gemini API Configuration</CardTitle>
              <CardDescription>Configure Google Gemini API for prompt generation</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="gemini-api-key">API Key</Label>
            <div className="relative">
              <Input
                id="gemini-api-key"
                type={showGeminiApi ? "text" : "password"}
                value={geminiApiKey}
                onChange={(e) => setGeminiApiKey(e.target.value)}
                placeholder="Enter your Gemini API key"
                className="pr-10"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute right-0 top-0 h-full px-3"
                onClick={() => setShowGeminiApi(!showGeminiApi)}
              >
                {showGeminiApi ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>
            </div>
            {geminiApiKey && (
              <div className="flex items-center gap-2 text-sm text-green-500">
                <CheckCircle className="h-4 w-4" />
                <span>API Key configured</span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Key className="h-4 w-4" />
            <span>Get your API key from <a href="https://makersuite.google.com/app/apikey" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Google AI Studio</a></span>
          </div>
        </CardContent>
      </Card>

      {/* Telegram Notification Settings */}
      <Card variant="glass">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center">
              <Send className="h-5 w-5 text-blue-500" />
            </div>
            <div>
              <CardTitle>Telegram Notification</CardTitle>
              <CardDescription>รับแจ้งเตือนเมื่อมีการเติมเครดิต</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="telegram-bot-token">Bot Token</Label>
            <div className="relative">
              <Input
                id="telegram-bot-token"
                type={showTelegramToken ? "text" : "password"}
                value={telegramBotToken}
                onChange={(e) => setTelegramBotToken(e.target.value)}
                placeholder="123456789:ABCdefGHIjklMNOpqrsTUVwxyz"
                className="pr-10"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute right-0 top-0 h-full px-3"
                onClick={() => setShowTelegramToken(!showTelegramToken)}
              >
                {showTelegramToken ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="telegram-chat-id">Chat ID</Label>
            <Input
              id="telegram-chat-id"
              value={telegramChatId}
              onChange={(e) => setTelegramChatId(e.target.value)}
              placeholder="-1001234567890 หรือ @channel_username"
            />
          </div>
          {telegramBotToken && telegramChatId && (
            <div className="flex items-center gap-2 text-sm text-green-500">
              <CheckCircle className="h-4 w-4" />
              <span>Telegram configured</span>
            </div>
          )}
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Key className="h-4 w-4" />
            <span>สร้าง Bot ที่ <a href="https://t.me/BotFather" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">@BotFather</a></span>
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={isSaving} className="gap-2">
          {isSaving ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Save className="h-4 w-4" />
          )}
          {isSaving ? "Saving..." : "Save API Settings"}
        </Button>
      </div>
    </div>
  );
};

export default ApiSettings;
